
import { defineComponent, reactive, onActivated, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Props } from '@loca/mfe-vue3'
import { Toast, Tabs, Tab } from 'vant'
import Empty from '@/components/Empty/index.vue'
import { MenuItem } from '@/models/Menu.model'
import { getMenuList } from '@/utils/menuList'
import { addClickHistory, filterMenuListByClickHistory } from '@/utils/MenuClickHistoryUtils'
import Analytics from '@/core/Analytics'
import { WEB_PERFORMANCE_CORE_SCENE_MAP } from '@/core/Analytics/config'

export default defineComponent({
  name: 'WorkbenchPage',

  components: {
    Empty,
    'van-tabs': Tabs,
    'van-tab': Tab
  },

  setup() {
    const { t } = useI18n()
    const router = useRouter()

    const state = reactive<{
      // 工作台菜单配置
      menuList: MenuItem[]
      // 工作台菜单，不带最近使用
      menuListWithoutHistory: MenuItem[]
      active: number
      inited: boolean
    }>({
      menuList: [],
      menuListWithoutHistory: [],
      inited: false,
      active: 0 as number
    })

    const genTabOffsetTop = () => {
      const { getRootProps } = Props
      const { systemInfo = {} } = getRootProps()
      return systemInfo.safeAreaTop || undefined
    }

    const vanTabsOffsetTop = () => {
      // van-tabs本身只支持页面级别的滚动，不支持局部滚动。滚动时会尝试把 目标DOM 滚到页面的顶部。
      // 其计算滚动距离时会以视图窗口的左上角来计算，且 van-tabs
      // 会自动减去 tabHeight，所以这里的offsetTop应该是页面左上角到tab的距离。
      // 即：滚动距离 = 页面左上角到目标DOM距离(A) - tabHeight(B) - 页面左上角到tab的距离(C)
      // 页面左上角到tab的距离(C) 就是offsetTop的大小
      return 44 + (genTabOffsetTop() || 0)
    }

    const genClickHistoryTab = (childArr: MenuItem[]) => {
      const clickHistoryTab: MenuItem = {
        menuId: '__defaultClickHistoryTabMenuId',
        name: t('workbenchRecentUsed') as string,
        visible: true,
        children: []
      }
      if (Array.isArray(childArr) && childArr.length) {
        clickHistoryTab.children = childArr
      }
      return clickHistoryTab
    }

    const assembleMenuList = (list: MenuItem[]) => {
      const filteredList = filterMenuListByClickHistory(list)
      const historyTab = genClickHistoryTab(filteredList)
      const newList = [historyTab, ...list]
      return newList
    }

    const updateMenuList = async () => {
      const list = await getMenuList()
      state.menuList = assembleMenuList(list)
      state.menuListWithoutHistory = list
      state.inited = true
    }

    const handleClickUrl = (menu: MenuItem) => {
      if (!menu.link) {
        return
      }
      // 添加到历史
      addClickHistory([menu.link])
      // 更新当前menu list
      state.menuList = assembleMenuList(state.menuListWithoutHistory)

      router.push(menu.link).catch(() => {})
    }

    onMounted(async () => {
      Toast.loading({
        message: t('loading') as string,
        forbidClick: false,
        duration: 30000
      })

      await updateMenuList()
      Toast.clear()
      Analytics.trackCoreScene(WEB_PERFORMANCE_CORE_SCENE_MAP.workbench)
    })

    onActivated(async () => {
      if (state.inited) {
        await updateMenuList()
        Toast.clear()
      }
    })

    return {
      t,
      state,
      handleClickUrl,
      vanTabsOffsetTop
    }
  }
})
